import React, { useState, useMemo, useEffect } from 'react';
import { useFlagStore } from '../lib/useFlagStore.js';
import { FLAG_REGISTRY } from '../lib/flags.js';
import { useDocumentTitle } from '../lib/useDocumentTitle.js';
import './feature-flags.css';

function Toggle({ checked, onChange, label }) {
  const handle = (e) => {
    if (e.type === 'click' || e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(!checked);
    }
  };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={handle}
      onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && handle(e)}
      className={`ff-lever ${checked ? 'on' : 'off'}`}
    >
      <span className="ff-lever-track">
        <span className="ff-lever-knob" />
      </span>
      <span className="ff-lever-state">{checked ? 'ON' : 'OFF'}</span>
    </button>
  );
}

function TypePill({ type }) {
  return <span className={`ff-pill ff-pill-${type}`}>{type}</span>;
}

function FlagRow({ flag, value, isOverridden, onToggle }) {
  return (
    <div className={`ff-row${isOverridden ? ' is-overridden' : ''}`}>
      <TypePill type={flag.type} />
      <div className="ff-row-body">
        <div className="ff-row-label">
          {flag.label}
          {isOverridden && <span className="ff-row-override-mark" title="Overridden from default">●</span>}
        </div>
        <div className="ff-row-desc">{flag.description}</div>
        <div className="ff-row-id">{flag.id}</div>
      </div>
      <Toggle checked={value} onChange={onToggle} label={flag.label} />
    </div>
  );
}

function GroupSection({ group, values, overrides, onToggle, onSetGroupMocks, expanded, onToggleExpand }) {
  const activeCount = group.flags.filter((f) => values[f.id]).length;
  const overrideCount = group.flags.filter((f) => f.id in overrides).length;
  const mockFlags = group.flags.filter((f) => f.type === 'mock');
  const allMocksOn = mockFlags.length > 0 && mockFlags.every((f) => values[f.id]);
  const allMocksOff = mockFlags.length > 0 && mockFlags.every((f) => !values[f.id]);
  return (
    <section className="ff-group">
      <div className="ff-group-head-row">
        <button
          type="button"
          className="ff-group-head"
          onClick={onToggleExpand}
          aria-expanded={expanded}
        >
          <span className={`ff-group-caret${expanded ? ' open' : ''}`}>▸</span>
          <span className="ff-group-title">{group.label}</span>
          <span className="ff-group-blurb">{group.blurb}</span>
          <span className="ff-group-stats">
            <span>{activeCount}<span className="ff-group-stat-of">/{group.flags.length}</span> on</span>
            {overrideCount > 0 && (
              <span className="ff-group-stat-override">{overrideCount} override{overrideCount === 1 ? '' : 's'}</span>
            )}
          </span>
        </button>
        {mockFlags.length > 0 && (
          <div className="ff-group-bulk" role="group" aria-label={`Bulk mock toggles for ${group.label}`}>
            <button
              type="button"
              className="ff-btn-mini"
              onClick={(e) => { e.stopPropagation(); onSetGroupMocks(group.id, true); }}
              disabled={allMocksOn}
              title={`Turn ON all ${mockFlags.length} mock flag${mockFlags.length === 1 ? '' : 's'} in ${group.label}`}
            >
              All mocks on
            </button>
            <button
              type="button"
              className="ff-btn-mini"
              onClick={(e) => { e.stopPropagation(); onSetGroupMocks(group.id, false); }}
              disabled={allMocksOff}
              title={`Turn OFF all ${mockFlags.length} mock flag${mockFlags.length === 1 ? '' : 's'} in ${group.label}`}
            >
              All mocks off
            </button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="ff-group-body">
          {group.flags.map((flag) => (
            <FlagRow
              key={flag.id}
              flag={flag}
              value={values[flag.id]}
              isOverridden={flag.id in overrides}
              onToggle={(next) => onToggle(flag.id, next)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function FeatureFlagsPage() {
  // JOBE-0193: per-route document title.
  useDocumentTitle('Feature Flags — Admin');
  const overrides = useFlagStore((s) => s.overrides);
  const setFlag = useFlagStore((s) => s.setFlag);
  const setManyFlags = useFlagStore((s) => s.setManyFlags);
  const resetFlags = useFlagStore((s) => s.resetFlags);

  const [expanded, setExpanded] = useState(() =>
    Object.fromEntries(FLAG_REGISTRY.map((g) => [g.id, true]))
  );
  const [filter, setFilter] = useState('');
  const [resetToast, setResetToast] = useState(null);

  const values = useMemo(() => {
    const out = {};
    for (const group of FLAG_REGISTRY) {
      for (const flag of group.flags) {
        out[flag.id] = flag.id in overrides ? overrides[flag.id] : flag.defaultValue;
      }
    }
    return out;
  }, [overrides]);

  const handleToggle = (id, next) => setFlag(id, next);

  const handleSetAllMocks = (next) => {
    const ids = FLAG_REGISTRY.flatMap((g) => g.flags.filter((f) => f.type === 'mock').map((f) => f.id));
    setManyFlags(ids, next);
  };

  const handleSetGroupMocks = (groupId, next) => {
    const group = FLAG_REGISTRY.find((g) => g.id === groupId);
    if (!group) return;
    const ids = group.flags.filter((f) => f.type === 'mock').map((f) => f.id);
    setManyFlags(ids, next);
  };

  const handleReset = () => {
    resetFlags();
    setResetToast(Date.now());
  };

  useEffect(() => {
    if (resetToast === null) return;
    const t = setTimeout(() => setResetToast(null), 2000);
    return () => clearTimeout(t);
  }, [resetToast]);

  const filteredGroups = useMemo(() => {
    const f = filter.toLowerCase().trim();
    if (!f) return FLAG_REGISTRY;
    return FLAG_REGISTRY
      .map((group) => ({
        ...group,
        flags: group.flags.filter(
          (flag) =>
            flag.label.toLowerCase().includes(f) ||
            flag.description.toLowerCase().includes(f) ||
            flag.id.toLowerCase().includes(f) ||
            group.label.toLowerCase().includes(f)
        ),
      }))
      .filter((g) => g.flags.length > 0);
  }, [filter]);

  const overrideCount = Object.keys(overrides).length;
  const totalFlags = FLAG_REGISTRY.reduce((n, g) => n + g.flags.length, 0);
  const activeFlags = Object.values(values).filter(Boolean).length;

  const allMockFlags = FLAG_REGISTRY.flatMap((g) => g.flags.filter((f) => f.type === 'mock'));
  const allMocksOn = allMockFlags.length > 0 && allMockFlags.every((f) => values[f.id]);
  const allMocksOff = allMockFlags.length > 0 && allMockFlags.every((f) => !values[f.id]);

  return (
    <div className="ff-page">
      <div className="ff-page-head">
        <div className="ff-page-head-left">
          <div className="ff-kicker">JOBE-0157</div>
          <h2 className="ff-page-title">Feature Flags</h2>
          <div className="ff-page-sub">
            Runtime toggles for in-progress UI and mock data sources. Local to this browser; not visible to other users.
          </div>
        </div>
        <div className="ff-page-head-right">
          {resetToast && <span className="ff-toast">Reset — defaults restored</span>}
          <button
            type="button"
            className="ff-btn-ghost"
            onClick={() => handleSetAllMocks(true)}
            disabled={allMocksOn}
            title={`Turn ON all ${allMockFlags.length} mock flags across all groups`}
          >
            All mocks on
          </button>
          <button
            type="button"
            className="ff-btn-ghost"
            onClick={() => handleSetAllMocks(false)}
            disabled={allMocksOff}
            title={`Turn OFF all ${allMockFlags.length} mock flags across all groups`}
          >
            All mocks off
          </button>
          <button
            type="button"
            className="ff-btn-ghost"
            onClick={handleReset}
            disabled={overrideCount === 0}
            title={overrideCount === 0 ? 'Nothing to reset' : `Clear ${overrideCount} override${overrideCount === 1 ? '' : 's'}`}
          >
            Reset to defaults
          </button>
        </div>
      </div>

      <div className="ff-summary-strip">
        <div className="ff-stat">
          <span className="ff-stat-label">Active</span>
          <span className="ff-stat-value">{activeFlags}<span className="ff-stat-of">/{totalFlags}</span></span>
        </div>
        <div className="ff-stat">
          <span className="ff-stat-label">Overrides</span>
          <span className={`ff-stat-value${overrideCount > 0 ? ' is-hot' : ''}`}>{overrideCount}</span>
        </div>
        <div className="ff-stat">
          <span className="ff-stat-label">Persisted to</span>
          <span className="ff-stat-value-mono">localStorage</span>
        </div>
        <div className="ff-summary-spacer" />
        <div className="ff-filter">
          <span className="ff-filter-icon" aria-hidden="true">⌕</span>
          <input
            type="search"
            placeholder="Filter by name, id, or description…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="ff-filter-input"
          />
          {filter && (
            <button
              type="button"
              onClick={() => setFilter('')}
              className="ff-filter-clear"
              aria-label="Clear filter"
            >×</button>
          )}
        </div>
      </div>

      <div className="ff-groups">
        {filteredGroups.map((group) => (
          <GroupSection
            key={group.id}
            group={group}
            values={values}
            overrides={overrides}
            onToggle={handleToggle}
            onSetGroupMocks={handleSetGroupMocks}
            expanded={expanded[group.id] ?? true}
            onToggleExpand={() =>
              setExpanded((p) => ({ ...p, [group.id]: !(p[group.id] ?? true) }))
            }
          />
        ))}
        {filteredGroups.length === 0 && (
          <div className="ff-empty">No flags match "{filter}".</div>
        )}
      </div>

      <footer className="ff-footer">
        <span className="ff-footer-mono">Registry: src/web/ui/src/lib/flags.js</span>
        <span className="ff-footer-mono">Store: useFlagStore (Zustand persist) · jobe.flags</span>
      </footer>
    </div>
  );
}
